import { useEffect, useMemo, useState } from "react";
import type { CSSProperties } from "react";
import { useCustom } from "@refinedev/core";
import { Select, Spin, Typography } from "antd";

const { Text } = Typography;
import debounce from "lodash/debounce";
import { apiUrl } from "@/consts";

type ClubOption = { id: string; name: string };
type TeamOption = { id: string; name: string };

export type ClubTeamValue = {
  clubId: string | null;
  teamId: string | null;
};

/**
 * Club search field + dependent team select.
 *
 * - The club field is a searchable async select backed by `GET /admin/clubs?q=`.
 * - The team field is disabled until a club is chosen; on club select it loads
 *   `GET /admin/clubs/:clubId/teams` and becomes enabled. Clearing the club resets
 *   and re-disables the team field.
 *
 * Both ids are surfaced through `onChange` so the parent form can submit them in the
 * create/edit payload.
 */
export default function ClubTeamSelect({
  value,
  onChange,
  initialClubName,
  initialTeamName,
  style,
}: {
  value: ClubTeamValue;
  onChange: (next: ClubTeamValue) => void;
  /** Pre-populates the club select label when editing an attached event. */
  initialClubName?: string | null;
  /** Pre-populates the team select label when editing an attached event. */
  initialTeamName?: string | null;
  style?: CSSProperties;
}) {
  // `clubInput` tracks what's typed right now; `clubSearch` is the debounced
  // value that actually drives the request. Keeping both lets us show the
  // spinner immediately on keystroke (during the debounce window) instead of
  // only once the network call starts.
  const [clubInput, setClubInput] = useState("");
  const [clubSearch, setClubSearch] = useState("");
  const [clubOptions, setClubOptions] = useState<ClubOption[]>([]);

  // Seed the club option list with the currently-attached club so its label renders
  // even before the user searches.
  useEffect(() => {
    if (value.clubId && initialClubName) {
      setClubOptions((prev) =>
        prev.some((c) => c.id === value.clubId)
          ? prev
          : [{ id: value.clubId!, name: initialClubName }, ...prev]
      );
    }
  }, [value.clubId, initialClubName]);

  const clubsQuery = useCustom<{ clubs: ClubOption[] }>({
    url: `${apiUrl}/clubs`,
    method: "get",
    config: { query: { q: clubSearch } },
    queryOptions: { enabled: clubSearch.trim().length >= 2 },
  });

  useEffect(() => {
    // The admin clubs endpoint wraps results as `{ clubs: [...] }`.
    const fetched = clubsQuery.data?.data?.clubs;
    if (Array.isArray(fetched)) {
      setClubOptions((prev) => {
        const byId = new Map<string, ClubOption>();
        // Keep the seeded/selected club so its label survives a new search.
        for (const c of prev) {
          if (c.id === value.clubId) byId.set(c.id, c);
        }
        for (const c of fetched) byId.set(c.id, c);
        return Array.from(byId.values());
      });
    }
  }, [clubsQuery.data, value.clubId]);

  const teamsQuery = useCustom<{ teams: TeamOption[] }>({
    url: `${apiUrl}/clubs/${value.clubId}/teams`,
    method: "get",
    queryOptions: { enabled: !!value.clubId },
  });

  const teamOptions: TeamOption[] = (() => {
    // The admin teams endpoint wraps results as `{ teams: [...] }`.
    const fetched = teamsQuery.data?.data?.teams;
    const list = Array.isArray(fetched) ? [...fetched] : [];
    // Ensure the currently-attached team renders before the list resolves.
    if (
      value.teamId &&
      initialTeamName &&
      !list.some((t) => t.id === value.teamId)
    ) {
      list.unshift({ id: value.teamId, name: initialTeamName });
    }
    return list;
  })();

  // Memoize so a re-render (e.g. from typing) doesn't drop the pending timer.
  const debouncedSetClubSearch = useMemo(
    () => debounce((v: string) => setClubSearch(v), 500),
    []
  );

  // Searching = the user has typed enough and results aren't settled yet —
  // either the debounce is still pending or the request is in flight.
  const clubSearching =
    clubInput.trim().length >= 2 &&
    (clubInput !== clubSearch || clubsQuery.isFetching);

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16, ...style }}>
      <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
        <Text strong>Club</Text>
        <Select
        allowClear
        showSearch
        placeholder="Search clubs by name..."
        optionFilterProp="label"
        filterOption={false}
        loading={clubSearching}
        value={value.clubId ?? undefined}
        style={{ width: "100%" }}
        options={clubOptions.map((c) => ({ value: c.id, label: c.name }))}
        onSearch={(v) => {
          setClubInput(v);
          debouncedSetClubSearch(v);
        }}
        onChange={(clubId: string | undefined) => {
          // Selecting or clearing a club always resets the team selection.
          onChange({ clubId: clubId ?? null, teamId: null });
        }}
        notFoundContent={
          clubSearching ? (
            <div style={{ textAlign: "center", padding: "8px 0" }}>
              <Spin size="small" />
            </div>
          ) : clubInput.trim().length >= 2 ? (
            "No clubs found"
          ) : (
            "Type at least 2 characters"
          )
        }
        />
      </div>

      <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
        <Text strong>Team</Text>
        <Select
        allowClear
        showSearch
        placeholder={value.clubId ? "Select a team..." : "Choose a club first"}
        optionFilterProp="label"
        disabled={!value.clubId}
        loading={!!value.clubId && (teamsQuery.isLoading || teamsQuery.isFetching)}
        value={value.teamId ?? undefined}
        style={{ width: "100%" }}
        options={teamOptions.map((t) => ({ value: t.id, label: t.name }))}
        onChange={(teamId: string | undefined) => {
          onChange({ clubId: value.clubId, teamId: teamId ?? null });
        }}
        />
      </div>
    </div>
  );
}
